import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useV2Api } from "../../../hooks/useV2Api";
import { friendlyErrorMessage } from "../../../utils/friendlyError";
import { adaptCompletionIssues } from "../workspace/preview/activityAdapter";
import "../shipments/V2Shipments.css";

function formatMoney(n, currency = "USD") {
	if (n == null) return "—";
	const num = typeof n === "string" ? parseFloat(n) : n;
	return num.toLocaleString("en-US", {
		style: "currency",
		currency,
		maximumFractionDigits: 2,
	});
}

function formatDate(d) {
	if (!d) return "—";
	return new Date(d).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function formatDateTime(d) {
	if (!d) return "—";
	return new Date(d).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function reliabilityPct(score) {
	const n = typeof score === "string" ? parseFloat(score) : score;
	return Math.max(0, Math.min(100, Math.round((n || 0) * 100)));
}

const STATUS_COLOR = {
	PENDING: "#8F8F85",
	RECEIVED: "#2B2560",
	INCOMPLETE: "#A67B2C",
	VALID: "#5C7A54",
	SELECTED: "#5C7A54",
	CONFIRMED: "#23201C",
	EXPIRED: "#8B3A3A",
	REJECTED: "#8B3A3A",
};

export default function V2QuoteDetail() {
	const navigate = useNavigate();
	const { id: shipmentId, quoteId } = useParams();
	const api = useV2Api();

	const [quote, setQuote] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.request(
				`/api/v2/shipments/${shipmentId}/quotes/${quoteId}`,
			);
			setQuote(res?.data || null);
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	}, [api, shipmentId, quoteId]);

	useEffect(() => {
		load();
	}, [load]);

	if (loading)
		return <div style={{ padding: "2rem", opacity: 0.6 }}>Loading quote…</div>;

	if (error && !quote) {
		return (
			<div>
				<button
					className="v2-btn v2-btn--outline"
					onClick={() => navigate(`/shipments/${shipmentId}`)}
					style={{ marginBottom: 12 }}
				>
					← Back to shipment
				</button>
				<div
					style={{
						padding: 16,
						borderRadius: 8,
						border: "1px solid #8B3A3A",
						background: "rgba(139,58,58,0.08)",
						color: "#8B3A3A",
					}}
				>
					<strong>Couldn't load quote.</strong>
					<div style={{ marginTop: 6, fontSize: 14 }}>
						{friendlyErrorMessage(error, "Failed to load quote")}
					</div>
				</div>
			</div>
		);
	}

	if (!quote) return null;

	const isTruck = quote.quote_type === "domestic_trucking";
	const breakdownItems = isTruck
		? [
				{
					label: "Per-mile rate",
					value: quote.per_mile_rate ? `$${quote.per_mile_rate}/mi` : null,
				},
				{ label: "Total miles", value: quote.total_miles || null },
			]
		: [
				{
					label: "Ocean freight",
					value: formatMoney(quote.ocean_freight, quote.currency),
				},
				{
					label: "Fuel surcharge",
					value: formatMoney(quote.fuel_surcharge, quote.currency),
				},
				{
					label: "Origin charges",
					value: formatMoney(quote.origin_charges, quote.currency),
				},
				{
					label: "Destination charges",
					value: formatMoney(quote.destination_charges, quote.currency),
				},
			];

	const aiConfidence = quote.ai_confidence;
	const aiParsed = quote.ai_parsed;
	const rawEmail = quote.raw_email_text;

	return (
		<div>
			<div className="v2-page-header">
				<div>
					<button
						className="v2-btn v2-btn--outline"
						onClick={() => navigate(`/shipments/${shipmentId}`)}
						style={{ marginBottom: 12, padding: "6px 14px", fontSize: 12 }}
					>
						← Back to shipment
					</button>
					<h1 className="v2-page-title">{quote.provider_name}</h1>
					<p className="v2-page-subtitle">
						Quote ·{" "}
						<span
							className="v2-badge"
							style={{
								background: `${STATUS_COLOR[quote.status] || "#8F8F85"}14`,
								color: STATUS_COLOR[quote.status] || "#8F8F85",
							}}
						>
							{quote.status}
						</span>
						{quote.selected && (
							<span
								className="v2-badge"
								style={{
									background: "rgba(92,122,84,0.12)",
									color: "#5C7A54",
									marginLeft: 8,
								}}
							>
								Selected
							</span>
						)}
					</p>
				</div>
				<button
					className="v2-btn v2-btn--outline"
					onClick={() => navigate(`/shipments/${shipmentId}/compare`)}
				>
					Compare with other quotes
				</button>
			</div>

			<div className="v2-detail-grid">
				{/* Pricing */}
				<div className="v2-detail-section">
					<div className="v2-detail-section-header">
						<span className="v2-detail-section-title">Pricing</span>
					</div>
					<div className="v2-detail-section-body">
						<div
							className="v2-detail-row"
							style={{ fontSize: 16, fontWeight: 600 }}
						>
							<span className="v2-detail-label">Total</span>
							<span className="v2-detail-value">
								{formatMoney(quote.total_cost, quote.currency)}
							</span>
						</div>
						{breakdownItems.map((b) => (
							<div key={b.label} className="v2-detail-row">
								<span className="v2-detail-label">{b.label}</span>
								<span className="v2-detail-value">{b.value || "—"}</span>
							</div>
						))}
						<div className="v2-detail-row">
							<span className="v2-detail-label">Currency</span>
							<span className="v2-detail-value">{quote.currency || "USD"}</span>
						</div>
						<div className="v2-detail-row">
							<span className="v2-detail-label">All-in confirmed</span>
							<span className="v2-detail-value">
								{quote.all_in_confirmed ? "Yes" : "No"}
							</span>
						</div>
						{/* {(() => {
							const issues = adaptCompletionIssues(quote.completion_issues);
							if (issues.length === 0) return null;
							return (
								<div
									style={{
										marginTop: 8,
										padding: 8,
										borderRadius: 6,
										background: "rgba(166,123,44,0.1)",
										color: "#A67B2C",
										fontSize: 12,
									}}
								>
									Things to verify: {issues.join(", ")}
								</div>
							);
						})()} */}
					</div>
				</div>

				{/* Service */}
				<div className="v2-detail-section">
					<div className="v2-detail-section-header">
						<span className="v2-detail-section-title">Service</span>
					</div>
					<div className="v2-detail-section-body">
						<div className="v2-detail-row">
							<span className="v2-detail-label">Transit</span>
							<span className="v2-detail-value">
								{quote.transit_days ? `${quote.transit_days} days` : "—"}
							</span>
						</div>
						{!isTruck && (
							<>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Door-to-door</span>
									<span className="v2-detail-value">
										{quote.total_days ? `${quote.total_days} days` : "—"}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Vessel</span>
									<span className="v2-detail-value">
										{quote.vessel_name || "—"}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Voyage</span>
									<span className="v2-detail-value">
										{quote.voyage_number || "—"}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">ETD</span>
									<span className="v2-detail-value">
										{formatDate(quote.departure_date)}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">ETA</span>
									<span className="v2-detail-value">
										{formatDate(quote.arrival_date)}
									</span>
								</div>
							</>
						)}
						{isTruck && (
							<>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Truck type</span>
									<span className="v2-detail-value">
										{(quote.truck_type || "—").replace("_", " ")}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Pickup</span>
									<span className="v2-detail-value">
										{formatDate(quote.pickup_date)}
									</span>
								</div>
								<div className="v2-detail-row">
									<span className="v2-detail-label">Delivery</span>
									<span className="v2-detail-value">
										{formatDate(quote.delivery_date)}
									</span>
								</div>
							</>
						)}
						<div className="v2-detail-row">
							<span className="v2-detail-label">Valid until</span>
							<span className="v2-detail-value">
								{formatDate(quote.valid_until)}
							</span>
						</div>
					</div>
				</div>

				{/* Provider */}
				<div className="v2-detail-section">
					<div className="v2-detail-section-header">
						<span className="v2-detail-section-title">Provider</span>
					</div>
					<div className="v2-detail-section-body">
						<div className="v2-detail-row">
							<span className="v2-detail-label">Name</span>
							<span className="v2-detail-value">{quote.provider_name}</span>
						</div>
						<div className="v2-detail-row">
							<span className="v2-detail-label">Email</span>
							<span className="v2-detail-value">{quote.provider_email}</span>
						</div>
						{quote.provider_contact_name && (
							<div className="v2-detail-row">
								<span className="v2-detail-label">Contact</span>
								<span className="v2-detail-value">
									{quote.provider_contact_name}
								</span>
							</div>
						)}
						<div className="v2-detail-row">
							<span className="v2-detail-label">Reliability</span>
							<span className="v2-detail-value">
								{reliabilityPct(quote.provider_reliability)}%
							</span>
						</div>
						{quote.provider_avg_response_time != null && (
							<div className="v2-detail-row">
								<span className="v2-detail-label">Avg response</span>
								<span className="v2-detail-value">
									{quote.provider_avg_response_time}h
								</span>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Notes / AI metadata / raw email — full-width section below the grid */}
			{(aiConfidence || aiParsed || rawEmail || quote.notes) && (
				<div className="v2-detail-section" style={{ marginTop: 16 }}>
					<div className="v2-detail-section-header">
						<span className="v2-detail-section-title">Source & AI parse</span>
					</div>
					<div className="v2-detail-section-body">
						<div className="v2-detail-row">
							<span className="v2-detail-label">Source</span>
							<span className="v2-detail-value">{quote.source || "EMAIL"}</span>
						</div>
						<div className="v2-detail-row">
							<span className="v2-detail-label">Extraction</span>
							<span className="v2-detail-value">
								{quote.extraction_source || "regex+AI"}
							</span>
						</div>
						{aiConfidence && (
							<details style={{ marginTop: 12 }}>
								<summary
									style={{ cursor: "pointer", fontSize: 13, color: "#8F432F" }}
								>
									AI confidence per field
								</summary>
								<pre
									style={{
										background: "#FBFAF6",
										padding: 10,
										borderRadius: 6,
										fontSize: 11,
										marginTop: 8,
										overflow: "auto",
									}}
								>
									{JSON.stringify(aiConfidence, null, 2)}
								</pre>
							</details>
						)}
						{aiParsed && (
							<details style={{ marginTop: 12 }}>
								<summary
									style={{ cursor: "pointer", fontSize: 13, color: "#8F432F" }}
								>
									Parsed JSON (raw)
								</summary>
								<pre
									style={{
										background: "#FBFAF6",
										padding: 10,
										borderRadius: 6,
										fontSize: 11,
										marginTop: 8,
										overflow: "auto",
									}}
								>
									{JSON.stringify(aiParsed, null, 2)}
								</pre>
							</details>
						)}
						{rawEmail && (
							<details style={{ marginTop: 12 }}>
								<summary
									style={{ cursor: "pointer", fontSize: 13, color: "#8F432F" }}
								>
									Original email body ({rawEmail.length} chars)
								</summary>
								<pre
									style={{
										background: "#FBFAF6",
										padding: 10,
										borderRadius: 6,
										fontSize: 12,
										marginTop: 8,
										whiteSpace: "pre-wrap",
										overflow: "auto",
										maxHeight: 400,
									}}
								>
									{rawEmail}
								</pre>
							</details>
						)}
						{quote.notes && (
							<div style={{ marginTop: 12, fontSize: 13 }}>
								<strong>Operator notes:</strong> {quote.notes}
							</div>
						)}
						<div style={{ marginTop: 16, fontSize: 11, color: "#8F8F85" }}>
							Created {formatDateTime(quote.created_at)} · Updated{" "}
							{formatDateTime(quote.updated_at)}
							{quote.selected_at && (
								<>
									<br />
									Selected {formatDateTime(quote.selected_at)} —{" "}
									{quote.selection_reason}
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
